import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, Award, DollarSign, Target, Zap } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { valorisation, yearlyTotals, LICORNE_YEAR, preSeedInvestment } from '../mock';
import { formatCurrency, formatCompact, formatCompactMobile } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';

export default function ValorisationPage() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const fmt = (v) => isMobile ? formatCompactMobile(v) : formatCompact(v);
  
  const lastYear = yearlyTotals[yearlyTotals.length - 1] || {};
  const baiiaMargin = lastYear.revenus ? Math.round(lastYear.baiia / lastYear.revenus * 100) : 0;
  const y5Val = valorisation.find(v => v.annee === 'Y5') || {};
  const licorneVal = valorisation.find(v => v.annee === LICORNE_YEAR) || {};

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/valorisation" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {language === 'fr' ? 'Valorisation' : 'Valuation'}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* === VALORISATION PRÉ-SEED 500K === */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-2xl border-2 border-lime-500/40" data-testid="val-preseed-banner">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <span className="px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                    {language === 'fr' ? 'Valorisation Pré-seed actuelle' : 'Current Pre-seed Valuation'}
                  </span>
                  <h3 className="text-2xl font-bold text-white mt-2">5 000 000 $ CAD <span className="text-sm text-gray-400 font-normal">post-money implicite</span></h3>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'Levée actuelle' : 'Current raise'}</p>
                  <p className="text-2xl font-bold text-lime-400">{preSeedInvestment.total.toLocaleString()}$</p>
                  <p className="text-xs text-gray-300">≈ 10% equity</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-black/40 rounded-lg border border-lime-500/30">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">{language === 'fr' ? 'Levée pré-seed' : 'Pre-seed raise'}</p>
                  <p className="text-lg font-bold text-lime-400">500K $</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/30">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">% Equity</p>
                  <p className="text-lg font-bold text-cyan-400">~10%</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-orange-500/30">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">{language === 'fr' ? 'Valorisation impliquée' : 'Implied valuation'}</p>
                  <p className="text-lg font-bold text-orange-400">5,0M $</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4 pt-3 border-t border-lime-500/20">
                {language === 'fr'
                  ? 'Ronde seed prévue Février 2027 : ~10% de la valeur établie à ce moment-là, avec émission d\'actions en conséquence. La valorisation post-seed dépendra de la traction réelle (cible 750+750 établissements à M6).'
                  : 'Seed round planned February 2027: ~10% of the value established at that time, with corresponding share issuance. Post-seed valuation will depend on actual traction (target 750+750 institutions at M6).'}
              </p>
            </div>
            {/* Logique de valorisation */}
            <div className="p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border border-lime-500/30">
              <h3 className="text-2xl font-bold text-lime-400 mb-4">{language === 'fr' ? 'Logique de Valorisation' : 'Valuation Logic'}</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-6">
                {language === 'fr'
                  ? 'La présente grille de valorisation a été établie selon les critères reconnus en évaluation d\'entreprise conforme. Elle repose sur l\'analyse de la nature et de l\'historique du modèle, de son positionnement sectoriel, de la qualité et de la récurrence des revenus, de la capacité bénéficiaire, des actifs intangibles, du niveau de risque, des comparables pertinents ainsi que des approches reconnues de valorisation. Dans ce cadre, EcoLunch a été analysée comme une infrastructure transactionnelle institutionnelle — et non comme un simple logiciel — en raison de sa fonction Merchant of Record, de son intégration fintech, de sa profondeur opérationnelle, de ses coûts de sortie élevés, de sa rétention structurelle et de son effet réseau cumulatif. Les multiples ont été appliqués aux revenus reconnus de la plateforme, et non au GMV.'
                  : 'This valuation grid has been established according to recognized criteria in compliant business valuation. It is based on the analysis of the model\'s nature and history, sector positioning, revenue quality and recurrence, earning capacity, intangible assets, risk level, relevant comparables and recognized valuation approaches. In this framework, EcoLunch has been analyzed as an institutional transactional infrastructure — not a simple software — due to its Merchant of Record function, fintech integration, operational depth, high switching costs, structural retention and cumulative network effect. Multiples have been applied to the platform\'s recognized revenues, not to GMV.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-black/40 rounded-lg border border-lime-500/20 text-center">
                  <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Base d\'analyse' : 'Analysis Basis'}</p>
                  <p className="text-sm font-semibold text-lime-400">{language === 'fr' ? 'Évaluation conforme réalisée' : 'Compliant valuation performed'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/20 text-center">
                  <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Nature du modèle' : 'Model Nature'}</p>
                  <p className="text-sm font-semibold text-cyan-400">{language === 'fr' ? 'Infrastructure institutionnelle' : 'Institutional infrastructure'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-orange-500/20 text-center">
                  <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Facteurs clés' : 'Key Factors'}</p>
                  <p className="text-sm font-semibold text-orange-400">{language === 'fr' ? 'MOR, FinTech, rétention, effet réseau' : 'MOR, FinTech, retention, network effect'}</p>
                </div>
                <div className="p-3 bg-black/40 rounded-lg border border-lime-500/20 text-center">
                  <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Base du multiple' : 'Multiple Basis'}</p>
                  <p className="text-sm font-semibold text-lime-400">{language === 'fr' ? 'Revenus reconnus (pas GMV)' : 'Recognized revenues (not GMV)'}</p>
                </div>
              </div>
            </div>


            {/* Facteurs */}
            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-4">
                {language === 'fr' ? 'Facteurs de Valorisation' : 'Valuation Factors'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(language === 'fr' ? [
                  { icon: TrendingUp, label: 'Croissance repas/jour (MoM, YoY)', color: 'lime' },
                  { icon: Award, label: 'Rétention écoles/CSS (>95% cible)', color: 'cyan' },
                  { icon: Target, label: 'Logos enterprise (CSS)', color: 'orange' },
                  { icon: Zap, label: `Marge BAIIA (>${baiiaMargin - 5}% cible)`, color: 'lime' },
                  { icon: DollarSign, label: 'RAR récurrent (SaaS + abos)', color: 'cyan' },
                  { icon: TrendingUp, label: 'GMV transactionnel (FinTech)', color: 'orange' }
                ] : [
                  { icon: TrendingUp, label: 'Meals/day growth (MoM, YoY)', color: 'lime' },
                  { icon: Award, label: 'School/board retention (>95% target)', color: 'cyan' },
                  { icon: Target, label: 'Enterprise logos (school boards)', color: 'orange' },
                  { icon: Zap, label: `EBITDA margin (>${baiiaMargin - 5}% target)`, color: 'lime' },
                  { icon: DollarSign, label: 'Recurring ARR (SaaS + subs)', color: 'cyan' },
                  { icon: TrendingUp, label: 'Transactional GMV (FinTech)', color: 'orange' }
                ]).map((facteur, idx) => {
                  const Icon = facteur.icon;
                  return (
                    <div key={idx} className={`flex items-start gap-3 p-4 bg-black/40 rounded-lg border border-${facteur.color}-500/20`}>
                      <Icon className={`h-6 w-6 text-${facteur.color}-400 flex-shrink-0`} />
                      <span className="text-sm text-gray-300">{facteur.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Catalyseurs */}
            <div>
              <h3 className="text-xl font-bold text-lime-400 mb-4">
                {language === 'fr' ? 'Catalyseurs de Valeur' : 'Value Catalysts'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(language === 'fr' ? [
                  { label: 'Monopole régional', icon: Target },
                  { label: 'Données propriétaires', icon: TrendingUp },
                  { label: 'Network effects', icon: Zap },
                  { label: "Barrières à l'entrée", icon: Award }
                ] : [
                  { label: 'Regional monopoly', icon: Target },
                  { label: 'Proprietary data', icon: TrendingUp },
                  { label: 'Network effects', icon: Zap },
                  { label: 'Barriers to entry', icon: Award }
                ]).map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-black/40 rounded-lg border border-lime-500/20">
                      <Icon className="h-5 w-5 text-lime-400" />
                      <span className="text-sm text-gray-300">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
